import { Box, Link, makeStyles, Typography } from "@material-ui/core";
import HomeOutlined from "@material-ui/icons/HomeOutlined";
import MailOutlined from "@material-ui/icons/MailOutlined";
import SchoolOutlined from "@material-ui/icons/SchoolOutlined";
import Search from "@material-ui/icons/Search";
import ShowChart from "@material-ui/icons/ShowChart";
import React from "react";
import { useHistory } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { Chevron } from "@/components/Chevron";
import { AddNewPropertyModal } from "@/components/property/AddNewPropertyModal";
import { layoutTokens, semanticColors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import theme from "@/theme";

const useStyles = makeStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[24],
    padding: `${layoutTokens.space[24]}px ${layoutTokens.space[24]}px ${layoutTokens.space[32]}px`,
    [theme.breakpoints.up("md")]: { padding: `${layoutTokens.space[32]}px 0`, gap: layoutTokens.space[32] },
  },
  header: { display: "flex", flexDirection: "column", gap: layoutTokens.space[16] },
  title: { ...textStyles["PC/Heading/H2"], color: semanticColors["text/primary"] },
  lead: { ...textStyles["SP/Body/Large"], color: semanticColors["text/secondary"] },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: layoutTokens.space[16],
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: layoutTokens.space[24] },
    "@media (min-width: 1024px)": { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
  },
  card: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: layoutTokens.space[12],
    backgroundColor: semanticColors["surface/card"],
    borderRadius: layoutTokens.radius.lg,
    padding: layoutTokens.space[20],
    [theme.breakpoints.up("md")]: { padding: layoutTokens.space[24] },
  },
  cardHeader: { display: "flex", alignItems: "center", gap: layoutTokens.space[12] },
  circle: {
    width: 44,
    height: 44,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: layoutTokens.radius.full,
    backgroundColor: semanticColors["icon/default"],
    color: semanticColors["icon/inverse"],
  },
  cardTitle: { ...textStyles["SP/Heading/H3"], color: semanticColors["text/primary"] },
  description: {
    ...textStyles["SP/Body/Large"],
    color: semanticColors["text/secondary"],
    [theme.breakpoints.up("md")]: textStyles["PC/Body/Large"],
  },
  cardAction: { display: "flex", justifyContent: "flex-end", marginTop: "auto" },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: layoutTokens.space[8],
    ...textStyles["SP/Button/M"],
    color: semanticColors["text/link"],
    cursor: "pointer",
  },
  chevron: { position: "relative", display: "inline-flex", width: 6, height: 10, flexShrink: 0, "& svg": { position: "absolute", left: -7, top: -5 } },
});

const guideCards = [
  {
    title: "物件の管理ができます",
    icon: HomeOutlined,
    description: "物件を登録すると、毎月の収支・利回り・ローンの残りがひと目で分かります。数字は毎月ひとりでに更新されます。",
    action: "物件を登録する",
    path: undefined,
  },
  {
    title: "検討している物件をシミュレーションできます",
    icon: ShowChart,
    description: "検討中の物件を登録すると、買った場合の毎月の収支と、売ったときの手残りを年ごとに見られます。",
    action: "検討中の物件を登録する",
    path: "/add_new_property/consideration",
  },
  {
    title: "認定プランナーに相談できます",
    icon: Search,
    description: "条件を選んで、あなたに合う認定プランナーを探せます。相談は無料で行えます。",
    action: "プランナーを探す",
    path: "/agent_search",
  },
  {
    title: "プランナーから提案が届きます",
    icon: MailOutlined,
    description: "あなたの情報を見た認定プランナーから、物件の提案がメッセージで届きます。気になるプランナーへ相談できます。",
    action: "メッセージを見る",
    path: "/offer",
  },
  {
    title: "不動産投資の基本を学べます",
    icon: SchoolOutlined,
    description: "1章5分ほどで読める記事を、初級編から順に用意しています。用語が分からなくても進められます。",
    action: "学ぶを開く",
    path: "/learning",
  },
];

export const UsageGuidePage: React.FC = () => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  const history = useHistory();
  const [isAddPropertyOpen, setIsAddPropertyOpen] = React.useState(false);

  return (
    <BaseTemplate
      headerTitle={isMobile ? "使い方ガイド" : undefined}
      onBack={isMobile ? () => history.push("/mypage") : undefined}
      isPcV2
      isDense
      disableBottomPadding
    >
      <Box className={classes.body}>
        <Box className={classes.header}>
          {!isMobile && (
            <>
              <Link component={RouterLink} to="/mypage" className={classes.link} underline="none">
                <span className={classes.chevron}><Chevron direction="back" /></span>ホーム
              </Link>
              <Typography component="h1" className={classes.title}>使い方ガイド</Typography>
            </>
          )}
          <Typography className={classes.lead}>
            プロパリーでできることをご紹介します。<br />気になるものからお試しください。
          </Typography>
        </Box>
        <Box className={classes.grid}>
          {guideCards.map(({ title, icon: Icon, description, action, path }) => (
            <Box component="section" key={title} className={classes.card} aria-label={title}>
              <Box className={classes.cardHeader}>
                <Box className={classes.circle}><Icon aria-hidden="true" /></Box>
                <Typography component="h2" className={classes.cardTitle}>{title}</Typography>
              </Box>
              <Typography className={classes.description}>{description}</Typography>
              <Box className={classes.cardAction}>
                {path ? (
                  <Link component={RouterLink} to={path} className={classes.link} underline="none">
                    {action}<span className={classes.chevron}><Chevron /></span>
                  </Link>
                ) : (
                  <Link component="button" type="button" className={classes.link} underline="none" onClick={() => setIsAddPropertyOpen(true)}>
                    {action}<span className={classes.chevron}><Chevron /></span>
                  </Link>
                )}
              </Box>
            </Box>
          ))}
        </Box>
        <Typography className={classes.lead}>
          わからないことがあれば、設定の「不具合・ご要望」からご連絡ください。
        </Typography>
      </Box>
      <AddNewPropertyModal isOpen={isAddPropertyOpen} onClose={() => setIsAddPropertyOpen(false)} />
    </BaseTemplate>
  );
};
